<template>
  <div>
    <topsearch title="Admin / API Keys Yönetimi" />
    <div class="flex">
      <div class="w-72 border-r h-full" style="min-width:217px">
        <adminSidebar />
      </div>
      <div class="w-full p-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-2xl font-bold text-gray-800">API Keys</h2>
            <p class="text-sm text-gray-600 mt-1">
              API ile veri çekmek için kullanılan güvenlik anahtarları
            </p>
          </div>
          <div class="flex gap-2">
            <button
              @click="showDocs = !showDocs"
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              {{ showDocs ? 'Dokümantasyonu Gizle' : 'API Dokümantasyonu' }}
            </button>
            <button
              @click="showCreateModal = true"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Yeni API Key
            </button>
          </div>
        </div>

        <!-- API Documentation -->
        <div v-if="showDocs" class="mb-6 bg-white border rounded-lg p-6">
          <h3 class="text-lg font-bold text-gray-800 mb-4">🚀 API Kullanım Kılavuzu</h3>

          <!-- Base URL -->
          <div class="mb-6">
            <h4 class="font-semibold text-gray-800 mb-2">Base URL</h4>
            <code class="block bg-gray-100 px-4 py-2 rounded text-sm mb-2">{{ baseUrl }}</code>
            <div class="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
              <strong>💡 Not:</strong> URL formatı: <code class="bg-yellow-100 px-2 py-1 rounded">https://{kurum-adı}-app.crmtronline.net</code>
              <br>Örnek: <code class="bg-yellow-100 px-2 py-1 rounded">https://hbt-app.crmtronline.net</code>
            </div>
          </div>

          <!-- Authentication -->
          <div class="mb-6">
            <h4 class="font-semibold text-gray-800 mb-2">Authentication</h4>
            <p class="text-sm text-gray-600 mb-2">API key'inizi <code class="bg-gray-100 px-2 py-1 rounded">X-API-Key</code> header'ı ile gönderin:</p>
            <pre class="bg-gray-900 text-gray-100 px-4 py-3 rounded text-sm overflow-x-auto"><code>curl -H "X-API-Key: YOUR_API_KEY" {{ baseUrl }}/api/v1/leads/export</code></pre>
          </div>

          <!-- Endpoints -->
          <div class="mb-6">
            <h4 class="font-semibold text-gray-800 mb-3">📍 Endpoint'ler</h4>

            <!-- Export Endpoint -->
            <div class="mb-4 border-l-4 border-blue-500 pl-4">
              <h5 class="font-semibold text-gray-800 mb-2">1. Lead Export (Manuel Pagination)</h5>
              <p class="text-sm text-gray-600 mb-2"><code class="bg-gray-100 px-2 py-1 rounded">POST /api/v1/leads/export</code></p>
              <p class="text-sm text-gray-600 mb-2">Limit/offset ile sayfalama yaparak lead'leri çeker.</p>

              <div class="mt-3">
                <p class="text-xs font-medium text-gray-700 mb-1">Örnek İstek (cURL):</p>
                <pre class="bg-gray-900 text-gray-100 px-4 py-3 rounded text-xs overflow-x-auto"><code>curl -X POST {{ baseUrl }}/api/v1/leads/export \
  -H "X-API-Key: YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "limit": 1000,
    "offset": 0,
    "with_notes": true,
    "filters": [
      {
        "field": "status_id",
        "operator": "=",
        "value": 1
      }
    ]
  }'</code></pre>
              </div>

              <div class="mt-3">
                <p class="text-xs font-medium text-gray-700 mb-1">Örnek İstek (Python):</p>
                <pre class="bg-gray-900 text-gray-100 px-4 py-3 rounded text-xs overflow-x-auto"><code>import requests

url = "{{ baseUrl }}/api/v1/leads/export"
headers = {
    "X-API-Key": "YOUR_API_KEY",
    "Content-Type": "application/json"
}
data = {
    "limit": 1000,
    "offset": 0,
    "with_notes": True  # Default: True (notlarla birlikte)
}

response = requests.post(url, headers=headers, json=data)
leads = response.json()['data']
print(f"Toplam: {response.json()['meta']['total']} kayıt")</code></pre>
              </div>
            </div>

            <!-- Bulk Export Endpoint -->
            <div class="mb-4 border-l-4 border-green-500 pl-4">
              <h5 class="font-semibold text-gray-800 mb-2">2. Lead Bulk Export (Otomatik Pagination)</h5>
              <p class="text-sm text-gray-600 mb-2"><code class="bg-gray-100 px-2 py-1 rounded">POST /api/v1/leads/export-bulk</code></p>
              <p class="text-sm text-gray-600 mb-2">Tüm veriyi otomatik 10,000'lik parçalar halinde çeker. Büyük veri setleri için önerilir.</p>

              <div class="mt-3">
                <p class="text-xs font-medium text-gray-700 mb-1">Örnek İstek (cURL):</p>
                <pre class="bg-gray-900 text-gray-100 px-4 py-3 rounded text-xs overflow-x-auto"><code>curl -X POST {{ baseUrl }}/api/v1/leads/export-bulk \
  -H "X-API-Key: YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "with_notes": true,
    "filters": [
      {
        "field": "created_at",
        "operator": ">=",
        "value": "2024-01-01"
      }
    ]
  }'</code></pre>
              </div>

              <div class="mt-3">
                <p class="text-xs font-medium text-gray-700 mb-1">Örnek İstek (PHP):</p>
                <pre class="bg-gray-900 text-gray-100 px-4 py-3 rounded text-xs overflow-x-auto"><code>$apiKey = "YOUR_API_KEY";
$url = "{{ baseUrl }}/api/v1/leads/export-bulk";

$data = [
    'with_notes' => true,  // Default: true (notlarla birlikte)
    'filters' => []
];

$ch = curl_init($url);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'X-API-Key: ' . $apiKey,
    'Content-Type: application/json'
]);

$response = curl_exec($ch);
$result = json_decode($response, true);

echo "Toplam: {$result['meta']['total']} kayıt\n";
curl_close($ch);</code></pre>
              </div>
            </div>
          </div>

          <!-- Parameters -->
          <div class="mb-6">
            <h4 class="font-semibold text-gray-800 mb-3">📋 Parametreler</h4>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left font-medium text-gray-700">Parametre</th>
                    <th class="px-4 py-2 text-left font-medium text-gray-700">Tip</th>
                    <th class="px-4 py-2 text-left font-medium text-gray-700">Açıklama</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr>
                    <td class="px-4 py-2"><code class="text-blue-600">limit</code></td>
                    <td class="px-4 py-2">integer</td>
                    <td class="px-4 py-2">Sayfa başına kayıt sayısı (max: 10000)</td>
                  </tr>
                  <tr>
                    <td class="px-4 py-2"><code class="text-blue-600">offset</code></td>
                    <td class="px-4 py-2">integer</td>
                    <td class="px-4 py-2">Başlangıç noktası (pagination için)</td>
                  </tr>
                  <tr>
                    <td class="px-4 py-2"><code class="text-blue-600">with_notes</code></td>
                    <td class="px-4 py-2">boolean</td>
                    <td class="px-4 py-2"><code>true</code> = notlarla birlikte (Default), <code>false</code> = notlar olmadan (10x hızlı)</td>
                  </tr>
                  <tr>
                    <td class="px-4 py-2"><code class="text-blue-600">filters</code></td>
                    <td class="px-4 py-2">array</td>
                    <td class="px-4 py-2">Filtreleme kriterleri (field, operator, value)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Filter Operators -->
          <div class="mb-6">
            <h4 class="font-semibold text-gray-800 mb-3">🔍 Filter Operatörleri</h4>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <code class="text-blue-600">=</code> Eşittir<br>
                <code class="text-blue-600">!=</code> Eşit değil<br>
                <code class="text-blue-600">&gt;</code> Büyüktür<br>
                <code class="text-blue-600">&lt;</code> Küçüktür
              </div>
              <div>
                <code class="text-blue-600">IN</code> Liste içinde<br>
                <code class="text-blue-600">BETWEEN</code> Aralık<br>
                <code class="text-blue-600">LIKE</code> Metin arama
              </div>
            </div>
          </div>

          <!-- Performance Tips -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 class="font-semibold text-blue-800 mb-2">💡 Performans İpuçları</h4>
            <ul class="text-sm text-blue-700 space-y-1">
              <li>✅ Notlar varsayılan olarak dahildir. Hız için <code class="bg-blue-100 px-2 py-1 rounded">with_notes: false</code> kullanın (10x daha hızlı)</li>
              <li>✅ Büyük veri setleri (&gt;50K) için <code class="bg-blue-100 px-2 py-1 rounded">/export-bulk</code> kullanın</li>
              <li>✅ Filtreleri kullanarak veri setini daraltın</li>
              <li>❌ 10,000'den fazla limit kullanmayın</li>
              <li>❌ Saniyede 10'dan fazla request atmayın</li>
            </ul>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-12">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p class="text-gray-600 mt-2">Yükleniyor...</p>
        </div>

        <!-- API Keys List -->
        <div v-else-if="apiKeys.length > 0" class="space-y-4">
          <div
            v-for="key in apiKeys"
            :key="key.id"
            class="bg-white border rounded-lg p-5 hover:shadow-md transition-shadow"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-3 mb-2">
                  <h3 class="text-lg font-semibold text-gray-800">{{ key.name }}</h3>
                  <span
                    :class="key.active == 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                    class="px-2 py-1 text-xs font-medium rounded"
                  >
                    {{ key.active == 1 ? 'Aktif' : 'Pasif' }}
                  </span>
                  <span v-if="key.expires_at" :class="isExpired(key.expires_at) ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'" class="px-2 py-1 text-xs font-medium rounded">
                    {{ isExpired(key.expires_at) ? 'Süresi Dolmuş' : 'Süre: ' + formatDate(key.expires_at) }}
                  </span>
                  <span v-if="key.username" class="text-sm text-gray-500">
                    • {{ key.username }}
                  </span>
                </div>

                <!-- API Key -->
                <div class="flex items-center gap-2 mb-3">
                  <code class="bg-gray-100 px-3 py-1.5 rounded text-sm font-mono text-gray-700">
                    {{ showKeys[key.id] ? key.api_key : maskApiKey(key.api_key) }}
                  </code>
                  <button
                    @click="toggleShowKey(key.id)"
                    class="p-1.5 hover:bg-gray-100 rounded transition"
                    :title="showKeys[key.id] ? 'Gizle' : 'Göster'"
                  >
                    <svg v-if="!showKeys[key.id]" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  </button>
                  <button
                    @click="copyToClipboard(key.api_key)"
                    class="p-1.5 hover:bg-gray-100 rounded transition"
                    title="Kopyala"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>

                <!-- Metadata -->
                <div class="text-sm text-gray-600 space-y-1">
                  <p>
                    <span class="font-medium">Rate Limit:</span>
                    {{ key.rate_limit }}/dakika, {{ key.burst_limit }}/saniye
                  </p>
                  <p v-if="key.ip_whitelist && key.ip_whitelist.length > 0">
                    <span class="font-medium">IP Whitelist:</span>
                    {{ key.ip_whitelist.join(', ') }}
                  </p>
                  <p>
                    <span class="font-medium">Oluşturulma:</span>
                    {{ formatDate(key.created_at) }}
                  </p>
                  <p v-if="key.last_used_at">
                    <span class="font-medium">Son Kullanım:</span>
                    {{ formatDate(key.last_used_at) }}
                  </p>
                  <p v-else class="text-yellow-600">
                    <span class="font-medium">Henüz kullanılmadı</span>
                  </p>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex gap-2 ml-4">
                <button
                  @click="toggleStatus(key)"
                  :class="key.active == 1 ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'"
                  class="px-3 py-1.5 rounded text-sm font-medium transition"
                >
                  {{ key.active == 1 ? 'Pasif Yap' : 'Aktif Yap' }}
                </button>
                <button
                  @click="confirmDelete(key)"
                  class="px-3 py-1.5 bg-red-100 text-red-700 rounded text-sm font-medium hover:bg-red-200 transition"
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12 bg-gray-50 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 mx-auto text-gray-400 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <h3 class="text-lg font-medium text-gray-800 mb-1">Henüz API Key Yok</h3>
          <p class="text-gray-600 mb-4">İlk API key'inizi oluşturarak başlayın</p>
          <button
            @click="showCreateModal = true"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Yeni API Key Oluştur
          </button>
        </div>

        <!-- Create Modal -->
        <div v-if="showCreateModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 class="text-xl font-bold text-gray-800 mb-4">Yeni API Key Oluştur</h3>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                API Key Adı *
              </label>
              <input
                v-model="newKeyName"
                type="text"
                placeholder="Örn: Production API Key"
                class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Rate Limit (istek/dakika)
              </label>
              <input
                v-model.number="newKeyRateLimit"
                type="number"
                min="1"
                max="1000"
                class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Burst Limit (istek/saniye)
              </label>
              <input
                v-model.number="newKeyBurstLimit"
                type="number"
                min="1"
                max="100"
                class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Geçerlilik Süresi (Opsiyonel)
              </label>
              <input
                v-model="newKeyExpiresAt"
                type="datetime-local"
                class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div v-if="createdKeyData" class="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p class="text-sm text-yellow-800 font-medium mb-3">
                ⚠️ Bu bilgileri bir daha göremezsiniz! Lütfen güvenli bir yere kaydedin:
              </p>
              <div class="space-y-2">
                <div>
                  <p class="text-xs text-gray-600 mb-1">API Key:</p>
                  <div class="flex items-center gap-2">
                    <code class="flex-1 bg-white px-3 py-2 rounded text-xs font-mono text-gray-700 break-all">
                      {{ createdKeyData.api_key }}
                    </code>
                    <button
                      @click="copyToClipboard(createdKeyData.api_key)"
                      class="p-2 hover:bg-yellow-100 rounded"
                      title="Kopyala"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-yellow-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div>
                  <p class="text-xs text-gray-600 mb-1">API Secret:</p>
                  <div class="flex items-center gap-2">
                    <code class="flex-1 bg-white px-3 py-2 rounded text-xs font-mono text-gray-700 break-all">
                      {{ createdKeyData.api_secret }}
                    </code>
                    <button
                      @click="copyToClipboard(createdKeyData.api_secret)"
                      class="p-2 hover:bg-yellow-100 rounded"
                      title="Kopyala"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-yellow-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex gap-2 justify-end">
              <button
                @click="closeCreateModal"
                class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {{ createdKeyData ? 'Kapat' : 'İptal' }}
              </button>
              <button
                v-if="!createdKeyData"
                @click="createApiKey"
                :disabled="!newKeyName || creating"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ creating ? 'Oluşturuluyor...' : 'Oluştur' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SidebarVue from "../components/Ui/Admin/Sidebar.vue";
import http from "@/services/http";
import EventBus from "../components/Ui/EventBus";
import config from "@/config";

export default {
  name: "AdminApiKeys",
  components: { adminSidebar: SidebarVue },
  data() {
    return {
      loading: true,
      apiKeys: [],
      showKeys: {},
      showCreateModal: false,
      showDocs: false,
      newKeyName: "",
      newKeyRateLimit: 100,
      newKeyBurstLimit: 10,
      newKeyExpiresAt: "",
      createdKeyData: null,
      creating: false
    };
  },
  computed: {
    baseUrl() {
      return config.endpoint;
    }
  },
  mounted() {
    this.loadApiKeys();
  },
  methods: {
    async loadApiKeys() {
      this.loading = true;
      try {
        const response = await http.getApiKeys();
        if (response.data.success) {
          this.apiKeys = response.data.api_keys || [];
        }
      } catch (error) {
        console.error("API keys yüklenemedi:", error);
        EventBus.$emit("bottomMessage", {
          open: true,
          type: "error",
          title: "Hata",
          body: "API keys yüklenemedi"
        });
      } finally {
        this.loading = false;
      }
    },

    async createApiKey() {
      if (!this.newKeyName) return;

      this.creating = true;
      try {
        const payload = {
          name: this.newKeyName,
          rate_limit: this.newKeyRateLimit,
          burst_limit: this.newKeyBurstLimit
        };

        if (this.newKeyExpiresAt) {
          payload.expires_at = this.newKeyExpiresAt;
        }

        const response = await http.createApiKey(payload);

        if (response.data.success) {
          this.createdKeyData = {
            api_key: response.data.api_key,
            api_secret: response.data.api_secret
          };
          EventBus.$emit("bottomMessage", {
            open: true,
            type: "success",
            title: "Başarılı",
            body: "API key oluşturuldu. Lütfen API key ve secret'ı kaydedin!"
          });
          await this.loadApiKeys();
        }
      } catch (error) {
        console.error("API key oluşturulamadı:", error);
        EventBus.$emit("bottomMessage", {
          open: true,
          type: "error",
          title: "Hata",
          body: "API key oluşturulamadı"
        });
      } finally {
        this.creating = false;
      }
    },

    async toggleStatus(key) {
      try {
        const response = await http.toggleApiKeyStatus(key.id);
        if (response.data.success) {
          key.active = response.data.active;
          EventBus.$emit("bottomMessage", {
            open: true,
            type: "success",
            title: "Başarılı",
            body: `API key ${key.active == 1 ? 'aktif' : 'pasif'} yapıldı`
          });
        }
      } catch (error) {
        console.error("Durum değiştirilemedi:", error);
        EventBus.$emit("bottomMessage", {
          open: true,
          type: "error",
          title: "Hata",
          body: "Durum değiştirilemedi"
        });
      }
    },

    confirmDelete(key) {
      if (confirm(`"${key.name}" API key'ini silmek istediğinizden emin misiniz?`)) {
        this.deleteApiKey(key);
      }
    },

    async deleteApiKey(key) {
      try {
        const response = await http.deleteApiKey(key.id);
        if (response.data.success) {
          this.apiKeys = this.apiKeys.filter(k => k.id !== key.id);
          EventBus.$emit("bottomMessage", {
            open: true,
            type: "success",
            title: "Başarılı",
            body: "API key silindi"
          });
        }
      } catch (error) {
        console.error("API key silinemedi:", error);
        EventBus.$emit("bottomMessage", {
          open: true,
          type: "error",
          title: "Hata",
          body: "API key silinemedi"
        });
      }
    },

    toggleShowKey(id) {
      this.$set(this.showKeys, id, !this.showKeys[id]);
    },

    maskApiKey(key) {
      if (!key || key.length < 16) return key;
      return key.substring(0, 8) + "•".repeat(48) + key.substring(key.length - 8);
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        EventBus.$emit("bottomMessage", {
          open: true,
          type: "success",
          title: "Kopyalandı",
          body: "Panoya kopyalandı",
          duration: 2000
        });
      });
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleString("tr-TR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    isExpired(dateString) {
      if (!dateString) return false;
      return new Date(dateString) < new Date();
    },

    closeCreateModal() {
      this.showCreateModal = false;
      this.newKeyName = "";
      this.newKeyRateLimit = 100;
      this.newKeyBurstLimit = 10;
      this.newKeyExpiresAt = "";
      this.createdKeyData = null;
    }
  }
};
</script>
